import { api } from './api';

export type VipQuestionType = 'LIKERT' | 'YES_NO' | 'TEXT';

export interface VipQuestion {
  id: string;
  text: string;
  type: VipQuestionType;
  required: boolean;
  reverseScored: boolean;
}

export interface VipQuestionnaire {
  title: string;
  description: string | null;
  questions: VipQuestion[];
}

export interface VipSurveyTemplate extends VipQuestionnaire {
  id: string;
  isActive: boolean;
  intervalDays: 7 | 14 | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface VipSurveyRound {
  id: string;
  templateId: string;
  templateVersion: number;
  title: string;
  code: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  questionnaire: VipQuestionnaire;
  _count: { responses: number };
}

export interface VipSurveyUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string;
}

export interface VipSurveyResponse {
  id: string;
  userId: string;
  roundId: string;
  score: number | null;
  rawScore: number;
  maxScore: number;
  submittedAt: string;
  user: VipSurveyUser;
  round: Pick<VipSurveyRound, 'id' | 'title' | 'templateId' | 'templateVersion' | 'questionnaire'>;
  answers: Array<{ questionId: string; value: number | string | boolean }>;
}

export interface VipSurveyReport {
  rounds: VipSurveyRound[];
  responses: VipSurveyResponse[];
  summary: { responseCount: number; participantCount: number; averageScore: number | null };
}

export interface VipGrowthPoint {
  responseId: string;
  roundId: string;
  roundTitle: string;
  templateId: string;
  templateTitle: string;
  templateVersion: number;
  score: number | null;
  submittedAt: string;
}

export type VipTemplateInput = Pick<VipSurveyTemplate, 'title' | 'description' | 'questions' | 'intervalDays' | 'isActive'>;

const base = '/admin/vip-surveys';

export const vipSurveys = {
  templates: async () => (await api.get<VipSurveyTemplate[]>(`${base}/templates`)).data,
  saveTemplate: async (data: VipTemplateInput, id?: string) =>
    (id
      ? await api.patch<VipSurveyTemplate>(`${base}/templates/${id}`, data)
      : await api.post<VipSurveyTemplate>(`${base}/templates`, data)).data,
  rounds: async (templateId?: string) =>
    (await api.get<VipSurveyRound[]>(`${base}/rounds`, { params: { templateId } })).data,
  createRound: async (data: { templateId: string; title: string; startsAt?: string; endsAt?: string }) =>
    (await api.post<VipSurveyRound>(`${base}/rounds`, data)).data,
  updateRound: async (id: string, data: { isActive: boolean; endsAt?: string | null }) =>
    (await api.patch<VipSurveyRound>(`${base}/rounds/${id}`, data)).data,
  reports: async (params: { templateId?: string; roundId?: string; userId?: string }) =>
    (await api.get<VipSurveyReport>(`${base}/reports`, { params })).data,
  growth: async (userId: string, templateId?: string) =>
    (await api.get<VipGrowthPoint[]>(`${base}/users/${userId}/growth`, { params: { templateId } })).data,
};
